import { ZonedDateTime } from "@js-joda/core";
import { type FC, useMemo } from "react";
import { Alert } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import {
	canEditScheduledPost,
	Platform,
	type PostDto,
	PostStatus,
	SCHEDULE_EDIT_LOCK_MINUTES,
	xPostUrl,
	youtubeShortsUrl,
} from "shared";
import {
	useSuspensePosts,
	useSuspensePostWithSiblings,
} from "../calendar/usePosts";
import { TimeFormatter, useTime } from "../common/time/useTime";
import {
	ButtonDropdownMenu,
	type DropdownActionProps,
} from "../components/ButtonDropdownMenu";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { FormPost } from "./FormPost";
import { FormPostProvider, useFormPostContext } from "./FormPostContext";

const FormPostConsumer: FC = () => {
	const {
		post,
		editing,
		isPlanPaid,
		handleScheduleClick,
		handlePublishClick,
		handleSaveClick,
		tiktokPublishBlockedReason,
	} = useFormPostContext();

	const { format } = useTime();

	const formattedDate = format(
		TimeFormatter.Date,
		ZonedDateTime.parse((post?.scheduledAt || post?.updatedAt) ?? ""),
	);

	const title = `${formattedDate} ${formattedDate.length ? "|" : ""} ${post?.platform ?? ""}`;

	const isScheduleLocked = Boolean(post && !canEditScheduledPost(post));
	const publishDisabled = Boolean(tiktokPublishBlockedReason);
	const publishTitle = tiktokPublishBlockedReason;

	const dropdownMenuItems: Array<DropdownActionProps> = useMemo(
		() => [
			{
				label: "Publish Now",
				props: {
					onClick: handlePublishClick,
					disabled: publishDisabled,
					title: publishTitle,
				},
			},
			{
				label: "Schedule",
				props: {
					onClick: handleScheduleClick,
					disabled: publishDisabled,
					title: publishTitle,
				},
			},
		],
		[handleScheduleClick, handlePublishClick, publishDisabled, publishTitle],
	);

	return (
		<AppPage
			title={title}
			headerActions={
				editing &&
				post &&
				!isScheduleLocked &&
				post.status !== PostStatus.PUBLISHED && (
					<ButtonDropdownMenu
						main={
							post.status === PostStatus.DRAFT
								? {
										label: "Schedule",
										props: {
											onClick: handleScheduleClick,
											disabled: publishDisabled,
											title: publishTitle,
										},
									}
								: post.status === PostStatus.SCHEDULED
									? {
											label: "Publish Now",
											props: {
												onClick: handlePublishClick,
												disabled: publishDisabled,
												title: publishTitle,
											},
										}
									: {
											label: "Save",
											props: {
												icon: "save",
												onClick: () => {
													if (post) {
														handleSaveClick(post.id);
													}
												},
											},
										}
						}
						options={dropdownMenuItems}
					/>
				)
			}
		>
			<p>
				<Link to={LinkGen.Calendar()}>Back to Calendar</Link>
			</p>
			{post?.status === PostStatus.PUBLISHED &&
				post.platform === Platform.YOUTUBE &&
				post.refId && (
					<Alert variant="success">
						Your Short is live.{" "}
						<a
							href={youtubeShortsUrl(post.refId)}
							target="_blank"
							rel="noopener noreferrer"
						>
							View on YouTube
						</a>
					</Alert>
				)}
			{post?.status === PostStatus.PUBLISHED &&
				post.platform === Platform.X &&
				post.refId && (
					<Alert variant="success">
						Your X post is live.{" "}
						<a
							href={xPostUrl(post.refId)}
							target="_blank"
							rel="noopener noreferrer"
						>
							View on X
						</a>
					</Alert>
				)}
			{isScheduleLocked && (
				<Alert variant="info">
					Edits are locked within {SCHEDULE_EDIT_LOCK_MINUTES} minutes of the
					scheduled publish time. You can still delete this Short below to
					cancel it.
				</Alert>
			)}
			{post?.publishStatus === "retrying" && (
				<Alert variant="warning">
					{post.statusDetail ??
						"This post will be retried automatically. No action needed unless it keeps failing."}
				</Alert>
			)}
			{post?.publishStatus === "held" && (
				<Alert variant="warning">
					{post.statusDetail ??
						"This X post is held in the queue (spend cap or publishing paused). It will publish automatically when allowed again."}
				</Alert>
			)}
			{post?.status === PostStatus.FAILED && post.errorMessage && (
				<Alert variant="danger">{post.errorMessage}</Alert>
			)}
			{!isPlanPaid && (
				<Alert variant="warning">
					Posts are not published if you don't have a plan. Please view the{" "}
					<Link to={LinkGen.Settings.Billing()}>Billing Settings here</Link>!
				</Alert>
			)}
			<FormPost post={post} editing />
		</AppPage>
	);
};

export const PagePost = () => {
	const { id: stringID } = useParams();
	const ID = Number(stringID);
	const postIDs = useSuspensePostWithSiblings(ID);
	const posts = useSuspensePosts(postIDs.IDs);
	const { post, siblings } = posts.reduce<{
		post: PostDto | null;
		siblings: PostDto[];
	}>(
		(acc, val) => {
			if (val.id === ID) {
				acc.post = val;
			} else {
				acc.siblings.push(val);
			}
			return acc;
		},
		{ post: null, siblings: [] },
	);

	if (!post) {
		return null;
	}

	return (
		<FormPostProvider
			key={`post-context-${post?.id}`}
			editing
			post={post}
			siblings={siblings}
		>
			<FormPostConsumer />
		</FormPostProvider>
	);
};
